import { useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { useApi } from "shared/services/api/useApi";
import { useParams } from "react-router";

export const useAllUsers = () => {
    const { getDataAsync } = useApi();

    const getAllUsersApiCall = useCallback(async () => {
        const response = await getDataAsync("allUsers");

        const result = response.status === 204
            ? null
            : response.json();

        return result;

    }, [getDataAsync]);

    const allUsers = useQuery("allUsersList", getAllUsersApiCall);

    return useMemo(() => {
        return {
            allUsers,
        };
    }, [allUsers]);
};

const useClientContactLeads = () => {
    const { getDataAsync, putData, postData, deleteData } = useApi();
    const queryClient = useQueryClient();
    const { clientId } = useParams();

    const getClientContactLeadsApiCall = useCallback(async () => {
        const response = await getDataAsync("getContactLeadsByClientId", {
            urlParams: [{ name: "$clientId", value: clientId }],
        });

        const result = response.status === 204
            ? null
            : response.json();

        return result;

    }, [getDataAsync, clientId]);

    const clientContactLeads = useQuery(
        "clientContactLeadsList",
        getClientContactLeadsApiCall
    );

    const createClientContactLeadApiCall = useCallback(
        ({ body }) => {
            return postData("clientContactLeads", {
                body,
            });
        },
        [postData]
    );

    const updateClientContactLeadApiCall = useCallback(
        ({ body }) => {
            return putData("clientContactLeads", {
                body,
            });
        },
        [putData]
    );

    const deleteClientContactLeadApiCall = useCallback(
        ({ clientContactLeadId }) => {
            return deleteData("deleteClientContactLead", {
                urlParams: [{ name: "$id", value: clientContactLeadId }],
            });
        },
        [deleteData]
    );

    const mutationOptions = useMemo(() => {
        return {
            onSuccess: () => {
                queryClient.invalidateQueries("clientContactLeadsList");
            },
        };
    }, [queryClient]);

    const createClientContactLead = useMutation(
        createClientContactLeadApiCall,
        mutationOptions
    );
    const updateClientContactLead = useMutation(
        updateClientContactLeadApiCall,
        mutationOptions
    );
    const deleteClientContactLead = useMutation(
        deleteClientContactLeadApiCall,
        mutationOptions
    );

    return useMemo(() => {
        return {
            clientContactLeads,
            createClientContactLead,
            updateClientContactLead,
            deleteClientContactLead,
        };
    }, [
        clientContactLeads,
        createClientContactLead,
        updateClientContactLead,
        deleteClientContactLead,
    ]);
};

export default useClientContactLeads;
